import { useTheme } from '@repo/ui/components/theme-provider'
import { surfaceRadii } from '@repo/ui/lib/design-tokens'
import { cn } from '@repo/ui/lib/utils'
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Point } from 'geojson'
import type { GeoJSONSource, MapLayerMouseEvent, MapMouseEvent } from 'maplibre-gl'
import maplibregl from 'maplibre-gl'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  createMap,
  createMapStyle,
  escapeMapHtml,
  fitMapToPoints,
  invalidateMapSize,
  type LngLat,
  latLngToLngLat,
  syncMapWhenStyleIsReady,
} from '@/lib/maplibre'

type PlaceMarker = {
  id: string
  label: string
  addressText: string | null
  lat: string | null
  lng: string | null
}

type PolicyMarker = {
  id: string
  name: string
  isDefault: boolean
  centerAddressText: string | null
  centerLat: string | null
  centerLng: string | null
  softRadiusMiles: number
  hardRadiusMiles: number
  zctaCoverage?: Array<{
    postalCode: string
    overlapPercent: number
    excluded?: boolean
    geometry: GeoJsonPolygonGeometry
  }>
}

type GeoJsonPolygonGeometry =
  | {
      type: 'Polygon'
      coordinates: number[][][]
    }
  | {
      type: 'MultiPolygon'
      coordinates: number[][][][]
    }

interface ServiceAreaMapProps {
  policies: PolicyMarker[]
  selectedPolicyId?: string | null
  draftPolicy?: PolicyMarker | null
  places: PlaceMarker[]
  defaultCenter?: [number, number]
  className?: string
  viewportSignal?: string | number | boolean
  emptyStateLabel?: string
  onTogglePostalCode?: (postalCode: string) => void
}

type CoverageProperties = {
  postalCode: string
}

type PlaceProperties = {
  label: string
  addressText: string | null
}

const emptyCoverageCollection: FeatureCollection<Geometry, CoverageProperties> = {
  type: 'FeatureCollection',
  features: [],
}
const emptyPlaceCollection: FeatureCollection<Point, PlaceProperties> = {
  type: 'FeatureCollection',
  features: [],
}

export function ServiceAreaMap({
  policies,
  selectedPolicyId,
  draftPolicy,
  places,
  defaultCenter,
  className,
  viewportSignal,
  emptyStateLabel = 'Enter a ZIP code to center your coverage map.',
  onTogglePostalCode,
}: ServiceAreaMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const popupRef = useRef<maplibregl.Popup | null>(null)
  const [mapError, setMapError] = useState<string | null>(null)
  const fitPointsRef = useRef<LngLat[]>([])
  const zctaCoverageRef = useRef<PolicyMarker['zctaCoverage']>([])
  const defaultCenterRef = useRef(defaultCenter)
  const fallbackCenterRef = useRef<LngLat>(defaultCenter ?? [39.8283, -98.5795])
  const includedCoverageDataRef =
    useRef<FeatureCollection<Geometry, CoverageProperties>>(emptyCoverageCollection)
  const excludedCoverageDataRef =
    useRef<FeatureCollection<Geometry, CoverageProperties>>(emptyCoverageCollection)
  const placesDataRef = useRef<FeatureCollection<Point, PlaceProperties>>(emptyPlaceCollection)
  const initialIsDarkRef = useRef(false)
  const coverageDataRef = useRef<{
    key: string
    data: FeatureCollection<Geometry, CoverageProperties>
  }>({ key: '', data: emptyCoverageCollection })
  const { resolvedTheme } = useTheme()
  initialIsDarkRef.current = resolvedTheme === 'dark'

  const visiblePolicies = useMemo(() => {
    const withoutSelected = draftPolicy
      ? policies.filter((policy) => policy.id !== draftPolicy.id)
      : policies

    return draftPolicy ? [...withoutSelected, draftPolicy] : policies
  }, [draftPolicy, policies])

  const positionedPlaces = useMemo(
    () =>
      places
        .filter((place) => place.lat && place.lng)
        .map((place) => ({
          ...place,
          point: [Number(place.lat), Number(place.lng)] as [number, number],
        }))
        .filter((place) => Number.isFinite(place.point[0]) && Number.isFinite(place.point[1])),
    [places]
  )
  const positionedPolicies = useMemo(
    () =>
      visiblePolicies
        .filter((policy) => policy.centerLat && policy.centerLng)
        .map((policy) => ({
          ...policy,
          point: [Number(policy.centerLat), Number(policy.centerLng)] as [number, number],
        }))
        .filter((policy) => Number.isFinite(policy.point[0]) && Number.isFinite(policy.point[1])),
    [visiblePolicies]
  )
  const activePolicy = useMemo(
    () =>
      positionedPolicies.find((policy) => policy.id === selectedPolicyId) ??
      positionedPolicies[positionedPolicies.length - 1] ??
      null,
    [positionedPolicies, selectedPolicyId]
  )
  const fallbackCenter = useMemo(
    () => defaultCenter ?? ([39.8283, -98.5795] as [number, number]),
    [defaultCenter]
  )
  defaultCenterRef.current = defaultCenter
  fallbackCenterRef.current = fallbackCenter
  const coverageGeometryKey =
    activePolicy?.zctaCoverage?.map((zcta) => zcta.postalCode).join(':') ?? 'empty'
  zctaCoverageRef.current = activePolicy?.zctaCoverage ?? []
  if (coverageDataRef.current.key !== coverageGeometryKey) {
    const zctaCoverage = zctaCoverageRef.current

    coverageDataRef.current = {
      key: coverageGeometryKey,
      data: zctaCoverage?.length
        ? {
            type: 'FeatureCollection',
            features: zctaCoverage.map((zcta) => ({
              type: 'Feature',
              id: zcta.postalCode,
              geometry: zcta.geometry as Geometry,
              properties: {
                postalCode: zcta.postalCode,
              },
            })),
          }
        : emptyCoverageCollection,
    }
  }
  const coverageData = coverageDataRef.current.data
  const includedCoverageData = useMemo<FeatureCollection<Geometry, CoverageProperties>>(() => {
    const includedCoverage = activePolicy?.zctaCoverage?.filter((zcta) => !zcta.excluded) ?? []

    return includedCoverage.length
      ? {
          type: 'FeatureCollection',
          features: includedCoverage.map((zcta) => ({
            type: 'Feature',
            geometry: zcta.geometry as Geometry,
            properties: {
              postalCode: zcta.postalCode,
            },
          })),
        }
      : emptyCoverageCollection
  }, [activePolicy?.zctaCoverage])
  includedCoverageDataRef.current = includedCoverageData
  const excludedCoverageData = useMemo<FeatureCollection<Geometry, CoverageProperties>>(() => {
    const excludedCoverage =
      activePolicy?.zctaCoverage?.filter((zcta) => Boolean(zcta.excluded)) ?? []

    return excludedCoverage.length
      ? {
          type: 'FeatureCollection',
          features: excludedCoverage.map((zcta) => ({
            type: 'Feature',
            geometry: zcta.geometry as Geometry,
            properties: {
              postalCode: zcta.postalCode,
            },
          })),
        }
      : emptyCoverageCollection
  }, [activePolicy?.zctaCoverage])
  excludedCoverageDataRef.current = excludedCoverageData
  const placesData = useMemo<FeatureCollection<Point, PlaceProperties>>(
    () => ({
      type: 'FeatureCollection',
      features: positionedPlaces.map((place) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: latLngToLngLat(place.point),
        },
        properties: {
          label: place.label,
          addressText: place.addressText,
        },
      })),
    }),
    [positionedPlaces]
  )
  placesDataRef.current = placesData
  const fitPoints = useMemo<LngLat[]>(() => {
    const points: LngLat[] = []
    if (activePolicy) points.push(latLngToLngLat(activePolicy.point))
    for (const feature of coverageData.features) collectGeometryPoints(feature.geometry, points)
    for (const place of positionedPlaces) points.push(latLngToLngLat(place.point))
    return points
  }, [activePolicy, coverageData.features, positionedPlaces])
  fitPointsRef.current = fitPoints

  useEffect(() => {
    const container = containerRef.current
    if (!container || mapRef.current) return

    let map: maplibregl.Map
    try {
      map = createMap({
        container,
        center: fallbackCenterRef.current,
        zoom: defaultCenterRef.current ? 10 : 4,
        isDark: initialIsDarkRef.current,
      })
    } catch {
      setMapError('Coverage map is unavailable in this browser.')
      return
    }

    mapRef.current = map
    setMapError(null)

    map.on('load', () => {
      addServiceAreaSourcesAndLayers(map, initialIsDarkRef.current)
      setSourceData(map, 'coverage', includedCoverageDataRef.current)
      setSourceData(map, 'coverage-excluded', excludedCoverageDataRef.current)
      setSourceData(map, 'places', placesDataRef.current)
      fitMapToPoints({
        map,
        points: fitPointsRef.current,
        fallbackCenter: fallbackCenterRef.current,
        fallbackZoom: defaultCenterRef.current ? 10 : 4,
        maxZoom: 13,
        padding: 72,
      })
    })

    return () => {
      popupRef.current?.remove()
      popupRef.current = null
      map.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    map.setStyle(createMapStyle(resolvedTheme === 'dark'))
  }, [resolvedTheme])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    return syncMapWhenStyleIsReady(map, () => {
      addServiceAreaSourcesAndLayers(map, resolvedTheme === 'dark')
      setSourceData(map, 'coverage', includedCoverageData)
      setSourceData(map, 'coverage-excluded', excludedCoverageData)
      setSourceData(map, 'places', placesData)
    })
  }, [includedCoverageData, excludedCoverageData, placesData, resolvedTheme])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const getCoverageFeature = (event: MapMouseEvent) => {
      return findCoverageFeatureAtPoint(coverageData.features, [event.lngLat.lng, event.lngLat.lat])
    }

    const handleCoverageClick = (event: MapMouseEvent) => {
      if (!onTogglePostalCode) return
      const feature = getCoverageFeature(event)
      const postalCode = feature?.properties?.postalCode
      if (postalCode) onTogglePostalCode?.(postalCode)
    }
    const handleCoverageMove = (event: MapMouseEvent) => {
      map.getCanvas().style.cursor =
        onTogglePostalCode && getCoverageFeature(event) ? 'pointer' : ''
    }
    const handleCoverageLeave = () => {
      map.getCanvas().style.cursor = ''
    }
    const handlePlaceClick = (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0] as Feature<Point, PlaceProperties> | undefined
      if (!feature) return
      popupRef.current?.remove()
      popupRef.current = new maplibregl.Popup({ closeButton: false, maxWidth: '220px' })
        .setLngLat(event.lngLat)
        .setHTML(renderPlacePopup(feature.properties))
        .addTo(map)
    }

    map.on('click', handleCoverageClick)
    map.on('mousemove', handleCoverageMove)
    map.on('mouseleave', handleCoverageLeave)
    map.on('click', 'place-point', handlePlaceClick)
    return () => {
      map.off('click', handleCoverageClick)
      map.off('mousemove', handleCoverageMove)
      map.off('mouseleave', handleCoverageLeave)
      map.off('click', 'place-point', handlePlaceClick)
    }
  }, [coverageData.features, onTogglePostalCode])

  useEffect(() => {
    const map = mapRef.current
    const container = containerRef.current
    if (!map || !container) return

    void viewportSignal
    const frame = window.requestAnimationFrame(() => {
      invalidateMapSize(map)
      fitMapToPoints({
        map,
        points: fitPointsRef.current,
        fallbackCenter,
        fallbackZoom: defaultCenter ? 10 : 4,
        maxZoom: 13,
        padding: 72,
      })
    })
    const resizeObserver = new ResizeObserver(() => invalidateMapSize(map))
    const handleResize = () => invalidateMapSize(map)
    resizeObserver.observe(container)
    window.addEventListener('resize', handleResize)

    return () => {
      window.cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }, [defaultCenter, fallbackCenter, viewportSignal])

  return (
    <div
      className={cn(
        'relative z-0 h-full overflow-hidden border border-border/70',
        surfaceRadii.shell,
        className
      )}
    >
      <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(circle_at_top_left,color-mix(in_oklab,var(--primary)_10%,transparent),transparent_34%),radial-gradient(circle_at_bottom_right,color-mix(in_oklab,var(--ring)_8%,transparent),transparent_30%)]" />
      <div ref={containerRef} className="h-full min-h-[360px] w-full" />
      {mapError ? (
        <div className="absolute inset-0 z-20 grid place-items-center bg-card/92 p-6 text-center text-sm text-muted-foreground">
          {mapError}
        </div>
      ) : null}
      {activePolicy?.zctaCoverage?.length && onTogglePostalCode ? (
        <div className="pointer-events-none absolute left-4 top-4 z-20 rounded-[4px] border border-border/70 bg-card/92 px-3 py-2 text-xs text-muted-foreground shadow-[var(--app-shadow-soft)] backdrop-blur-xl">
          Click a boundary to exclude or reinclude it.
        </div>
      ) : null}
      {((activePolicy && !activePolicy.zctaCoverage?.length) || positionedPolicies.length === 0) &&
      emptyStateLabel.trim() ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 mx-auto w-fit rounded-[4px] border border-border/70 bg-card/92 px-4 py-2 text-sm text-muted-foreground shadow-[var(--app-shadow-soft)] backdrop-blur-xl">
          {emptyStateLabel}
        </div>
      ) : null}
    </div>
  )
}

function addServiceAreaSourcesAndLayers(map: maplibregl.Map, isDark: boolean) {
  if (!map.isStyleLoaded()) return

  const coverageFillColor = isDark ? '#b77962' : '#a66c55'
  const coverageLineColor = isDark ? '#c9927d' : '#965f49'
  const excludedFillColor = isDark ? '#c86f61' : '#c45f52'
  const excludedLineColor = isDark ? '#dc8f82' : '#b95d52'

  if (!map.getSource('coverage')) {
    map.addSource('coverage', { type: 'geojson', data: emptyCoverageCollection })
  }
  if (!map.getLayer('coverage-fill')) {
    map.addLayer({
      id: 'coverage-fill',
      type: 'fill',
      source: 'coverage',
      paint: {
        'fill-color': coverageFillColor,
        'fill-opacity': isDark ? 0.24 : 0.18,
      },
    })
  }
  map.setPaintProperty('coverage-fill', 'fill-color', coverageFillColor)
  map.setPaintProperty('coverage-fill', 'fill-opacity', isDark ? 0.24 : 0.18)
  if (!map.getLayer('coverage-line')) {
    map.addLayer({
      id: 'coverage-line',
      type: 'line',
      source: 'coverage',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': coverageLineColor,
        'line-opacity': isDark ? 0.74 : 0.62,
        'line-width': 0.78,
      },
    })
  }
  map.setLayoutProperty('coverage-line', 'line-cap', 'round')
  map.setLayoutProperty('coverage-line', 'line-join', 'round')
  map.setPaintProperty('coverage-line', 'line-color', coverageLineColor)
  map.setPaintProperty('coverage-line', 'line-opacity', isDark ? 0.74 : 0.62)
  map.setPaintProperty('coverage-line', 'line-width', 0.78)
  if (!map.getSource('coverage-excluded')) {
    map.addSource('coverage-excluded', { type: 'geojson', data: emptyCoverageCollection })
  }
  if (!map.getLayer('coverage-excluded-fill')) {
    map.addLayer({
      id: 'coverage-excluded-fill',
      type: 'fill',
      source: 'coverage-excluded',
      paint: {
        'fill-color': excludedFillColor,
        'fill-opacity': isDark ? 0.11 : 0.08,
      },
    })
  }
  map.setPaintProperty('coverage-excluded-fill', 'fill-color', excludedFillColor)
  map.setPaintProperty('coverage-excluded-fill', 'fill-opacity', isDark ? 0.11 : 0.08)
  if (!map.getLayer('coverage-excluded-line')) {
    map.addLayer({
      id: 'coverage-excluded-line',
      type: 'line',
      source: 'coverage-excluded',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': excludedLineColor,
        'line-dasharray': [0.25, 2.8],
        'line-opacity': isDark ? 0.76 : 0.66,
        'line-width': 1.15,
      },
    })
  }
  map.setLayoutProperty('coverage-excluded-line', 'line-cap', 'round')
  map.setLayoutProperty('coverage-excluded-line', 'line-join', 'round')
  map.setPaintProperty('coverage-excluded-line', 'line-color', excludedLineColor)
  map.setPaintProperty('coverage-excluded-line', 'line-dasharray', [0.25, 2.8])
  map.setPaintProperty('coverage-excluded-line', 'line-opacity', isDark ? 0.76 : 0.66)
  map.setPaintProperty('coverage-excluded-line', 'line-width', 1.15)
  if (!map.getSource('places')) {
    map.addSource('places', { type: 'geojson', data: emptyPlaceCollection })
  }
  if (!map.getLayer('place-point')) {
    map.addLayer({
      id: 'place-point',
      type: 'circle',
      source: 'places',
      paint: {
        'circle-radius': 5,
        'circle-color': '#4f83b8',
        'circle-stroke-color': '#f4f1eb',
        'circle-stroke-width': 2,
      },
    })
  }
}

function setSourceData(
  map: maplibregl.Map,
  sourceId: string,
  data: FeatureCollection<Geometry, GeoJsonProperties>
) {
  const source = map.getSource(sourceId) as GeoJSONSource | undefined
  source?.setData(data)
}

function collectGeometryPoints(geometry: Geometry, points: LngLat[]) {
  if (geometry.type === 'Polygon') {
    for (const ring of geometry.coordinates) {
      for (const coordinate of ring) points.push(coordinate as LngLat)
    }
  }
  if (geometry.type === 'MultiPolygon') {
    for (const polygon of geometry.coordinates) {
      for (const ring of polygon) {
        for (const coordinate of ring) points.push(coordinate as LngLat)
      }
    }
  }
}

function findCoverageFeatureAtPoint(
  features: Feature<Geometry, CoverageProperties>[],
  point: LngLat
) {
  return (
    features.find((feature) => {
      const { geometry } = feature
      if (geometry.type === 'Polygon') {
        return isPointInPolygon(point, geometry.coordinates as LngLat[][])
      }
      if (geometry.type === 'MultiPolygon') {
        return geometry.coordinates.some((polygon) =>
          isPointInPolygon(point, polygon as LngLat[][])
        )
      }
      return false
    }) ?? null
  )
}

function isPointInPolygon(point: LngLat, rings: LngLat[][]) {
  const [outerRing, ...holes] = rings
  if (!outerRing || !isPointInRing(point, outerRing)) return false
  return !holes.some((ring) => isPointInRing(point, ring))
}

function isPointInRing(point: LngLat, ring: LngLat[]) {
  const [x, y] = point
  let inside = false

  for (
    let index = 0, previousIndex = ring.length - 1;
    index < ring.length;
    previousIndex = index++
  ) {
    const [xi, yi] = ring[index]
    const [xj, yj] = ring[previousIndex]
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersects) inside = !inside
  }

  return inside
}

function renderPlacePopup(properties: PlaceProperties) {
  return `
    <div style="min-width: 150px; color: var(--card-foreground);">
      <div style="font-size: 14px; font-weight: 600;">${escapeMapHtml(properties.label)}</div>
      ${
        properties.addressText
          ? `<div style="margin-top: 6px; color: var(--muted-foreground); font-size: 13px; line-height: 1.45;">${escapeMapHtml(properties.addressText)}</div>`
          : ''
      }
    </div>
  `
}
